import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GitBranch, ExternalLink, CheckCircle2, UploadCloud, Loader2, GitPullRequest } from "lucide-react";
import { ProviderIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";

function PushResult({ result }) {
  return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5" data-testid="push-result">
        <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-display font-semibold">Pushed {result.files} files</span></div>
        <dl className="mt-3 space-y-1 font-mono-tight text-xs text-slate-700">
          <div className="flex gap-2"><dt className="w-16 text-slate-500">repo</dt><dd>{result.repo}</dd></div>
          <div className="flex gap-2"><dt className="w-16 text-slate-500">branch</dt><dd>{result.branch}</dd></div>
          <div className="flex gap-2"><dt className="w-16 text-slate-500">commit</dt><dd>{result.commit_sha?.slice(0, 10)}</dd></div>
          {result.pr_url && <div className="flex gap-2"><dt className="w-16 text-slate-500">PR</dt><dd>#{result.pr_number} → {result.base_branch}</dd></div>}
        </dl>
        <div className="mt-4 flex flex-wrap gap-4">
          <a href={result.tree_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 underline" data-testid="push-result-link">
            Open repository <ExternalLink className="h-3.5 w-3.5" />
          </a>
          {result.pr_url && (
            <a href={result.pr_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 underline" data-testid="push-result-pr-link">
              <GitPullRequest className="h-3.5 w-3.5" /> Open pull request #{result.pr_number}
            </a>
          )}
        </div>
      </div>
  );
}

function PushForm({ connectors, conn, form, setForm, pick, togglePr, busy, onSubmit, onCancel }) {
  return (
      <form onSubmit={onSubmit} className="space-y-4" data-testid="push-form">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Connector</Label>
          <div className="mt-2 grid grid-cols-1 gap-2">
            {connectors.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                className={cn(
                  "flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  form.connector_id === c.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400",
                )}
                data-testid={`push-connector-${c.name}`}
              >
                <span className="flex items-center gap-2"><ProviderIcon provider={c.provider} className="h-4 w-4" /><span className="font-medium">{c.name}</span></span>
                <span className={cn("font-mono-tight text-[10px] uppercase tracking-widest", form.connector_id === c.id ? "text-slate-300" : "text-slate-500")}>{c.last_test?.ok ? "connected" : "untested"}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">{conn?.provider === "azure_devops" ? "Org/Project" : "Owner"}</Label>
            <Input required data-testid="push-owner" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Repository</Label>
            <Input required data-testid="push-repo" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.repo_name} onChange={(e) => setForm({ ...form, repo_name: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Branch</Label>
            <Input required data-testid="push-branch" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Commit message</Label>
            <Input required data-testid="push-message" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.commit_message} onChange={(e) => setForm({ ...form, commit_message: e.target.value })} />
          </div>
        </div>

        <div className={cn("rounded-md border p-3 transition-colors", form.open_pr ? "border-slate-900 bg-slate-50" : "border-slate-200")} data-testid="pr-block">
          <label className="flex cursor-pointer items-center gap-3">
            <Checkbox checked={form.open_pr} onCheckedChange={(v) => togglePr(!!v)} data-testid="push-open-pr" />
            <span className="text-sm">
              <span className="font-medium text-slate-900">Open a pull request</span>
              <span className="ml-2 font-mono-tight text-[11px] text-slate-500">push to the feature branch, then request a review into the target branch</span>
            </span>
          </label>
          {form.open_pr && (
            <div className="mt-3 grid grid-cols-[120px_1fr] gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Target</Label>
                <Input data-testid="push-base-branch" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" placeholder="repo default" value={form.base_branch} onChange={(e) => setForm({ ...form, base_branch: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">PR title</Label>
                <Input required data-testid="push-pr-title" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.pr_title} onChange={(e) => setForm({ ...form, pr_title: e.target.value })} />
              </div>
            </div>
          )}
        </div>
        <p className="font-mono-tight text-[11px] text-slate-500">
          <GitBranch className="mr-1 inline h-3 w-3" /> {form.owner || "?"}/{form.repo_name || "?"} · existing repos get a new commit, missing ones are created ({conn?.visibility}).
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={busy || !form.connector_id} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="push-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />} {busy ? "Pushing…" : form.open_pr ? "Push & open PR" : "Push"}
          </Button>
        </div>
      </form>
  );
}

export function PushToGitDialog({ open, onOpenChange, project, onPushed }) {
  const [connectors, setConnectors] = useState(null);
  const [form, setForm] = useState({ connector_id: "", repo_name: "", owner: "", branch: "main", commit_message: "", open_pr: false, base_branch: "", pr_title: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    client.get("/connectors").then((r) => {
      setConnectors(r.data);
      const first = r.data[0];
      setForm({
        connector_id: first?.id || "",
        repo_name: project.name,
        owner: first?.default_owner || "",
        branch: "main",
        commit_message: `chore: scaffold ${project.name} with Toastern`,
        open_pr: false,
        base_branch: "",
        pr_title: `Toastern: scaffold ${project.name}`,
      });
    });
  }, [open, project.id, project.name]);

  const conn = connectors?.find((c) => c.id === form.connector_id);
  const pick = (c) => setForm((f) => ({ ...f, connector_id: c.id, owner: c.default_owner || f.owner }));
  const togglePr = (on) => setForm((f) => ({
    ...f,
    open_pr: on,
    branch: on && ["main", "master"].includes(f.branch) ? `toastern/${project.name}` : f.branch,
  }));

  const push = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post(`/projects/${project.id}/push`, form);
      setResult(data);
      onPushed?.(data);
      toast.success(data.repo_created ? "Repository created and project pushed" : "Project pushed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="push-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Push to Git</DialogTitle>
          <DialogDescription>Creates the repository if it does not exist, then commits every generated file.</DialogDescription>
        </DialogHeader>

        {connectors && connectors.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600" data-testid="push-no-connectors">
            No Git connector configured yet.
            <div className="mt-3"><Link to="/app/connectors" className="font-medium text-slate-900 underline" data-testid="push-go-connectors">Open Connectors</Link></div>
          </div>
        )}

        {result ? (
          <PushResult result={result} />
        ) : connectors?.length > 0 && (
          <PushForm connectors={connectors} conn={conn} form={form} setForm={setForm} pick={pick} togglePr={togglePr} busy={busy} onSubmit={push} onCancel={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
